import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, ArrowRight, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState(token ? 'verifying' : 'no-token');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!token) return;
    const verify = async () => {
      try {
        const res = await fetch(`${API_URL}/api/reader/verify-email?token=${token}`, { method: 'POST' });
        if (res.ok) {
          const data = await res.json();
          setEmail(data.email || '');
          setStatus('success');
        } else {
          setStatus('error');
        }
      } catch {
        setStatus('error');
      }
    };
    verify();
  }, [token]);

  return (
    <div className="bg-navy min-h-screen flex items-center justify-center px-5" data-testid="verify-email-page">
      <div className="max-w-md w-full">
        {status === 'verifying' && (
          <div className="text-center">
            <Loader2 size={32} className="mx-auto text-gold animate-spin mb-4" />
            <h1 className="font-serif text-2xl text-cream font-light tracking-tight mb-2">Verifying your email...</h1>
            <p className="text-cream/30 text-sm">Please wait a moment.</p>
          </div>
        )}
        {status === 'success' && (
          <div className="text-center" data-testid="verify-success">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={28} className="text-green-400" />
            </div>
            <h1 className="font-serif text-3xl text-cream font-light tracking-tight mb-3">Email Verified</h1>
            {email && <p className="text-cream/40 text-sm mb-6">{email} is now verified.</p>}
            <p className="text-cream/30 text-sm mb-8">You now have full access to like and comment on articles.</p>
            <Link to="/articles" className="btn-editorial border border-gold text-gold px-8 py-4 text-[13px] uppercase tracking-[0.15em] font-sans font-medium inline-flex items-center gap-3">
              <span>Browse Articles</span> <ArrowRight size={16} />
            </Link>
          </div>
        )}
        {status === 'error' && (
          <div className="text-center" data-testid="verify-error">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <AlertCircle size={28} className="text-red-400" />
            </div>
            <h1 className="font-serif text-3xl text-cream font-light tracking-tight mb-3">Verification Failed</h1>
            <p className="text-cream/30 text-sm mb-8">This link may have expired or already been used.</p>
            <Link to="/signin" className="text-gold text-sm hover:text-gold-light">Sign in to request a new link</Link>
          </div>
        )}
        {status === 'no-token' && (
          <div className="text-center">
            <Mail size={32} className="mx-auto text-cream/15 mb-4" />
            <h1 className="font-serif text-3xl text-cream font-light tracking-tight mb-3">Email Verification</h1>
            <p className="text-cream/30 text-sm mb-6">Check your inbox for the verification link.</p>
            <Link to="/" className="text-gold text-sm hover:text-gold-light">Back to Home</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
